import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { CriteriaFields } from './constants';
import { IconCircledCheck, IconCircledCross, IconCross } from '../../../../utils/icons';
import eventEmitter from '../../../../utils/eventEmitter';

const OPEN_EVENT = 'feedbackCriteria:open';
const AUTO_OPEN_DELAY = 1000;

export const openFeedbackCriteria = () => eventEmitter.emit(OPEN_EVENT);

const readSeen = (key: string) => {
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
};

const writeSeen = (key: string, seen: boolean) => {
  try {
    if (seen) localStorage.setItem(key, 'true');
    else localStorage.removeItem(key);
  } catch {
    // storage is unavailable in private browsing; the dialog still works
  }
};

const CriteriaColumn: FC<{ title: string; editable?: boolean }> = ({ title, editable }) => (
  <Box className="flex flex-col gap-4 w-full">
    <Typography variant="h6" className="!text-lg">
      {title}
    </Typography>
    {(editable ? CriteriaFields.editable : CriteriaFields.nonEditable).map((field) => (
      <Box className="flex items-center gap-2" key={field}>
        {editable ? <IconCircledCheck size={24} strokeWidth={5} /> : <IconCircledCross size={24} />}
        <Typography variant="body2">{field}</Typography>
      </Box>
    ))}
  </Box>
);

interface Props {
  autoShow?: boolean;
  seenKey?: string;
}

export const FeedbackCriteriaDialog: FC<Props> = ({ autoShow = false, seenKey }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleOpen = () => setOpen(true);
    eventEmitter.on(OPEN_EVENT, handleOpen);
    return () => {
      eventEmitter.off(OPEN_EVENT, handleOpen);
    };
  }, []);

  useEffect(() => {
    if (!seenKey) return undefined;

    if (!autoShow) {
      writeSeen(seenKey, false);
      return undefined;
    }

    if (readSeen(seenKey)) return undefined;

    writeSeen(seenKey, true);
    const timer = setTimeout(() => setOpen(true), AUTO_OPEN_DELAY);
    return () => clearTimeout(timer);
  }, [autoShow, seenKey]);

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
      <DialogTitle className="flex items-center justify-between">
        <Typography variant="h5">Feedback Criteria</Typography>
        <IconButton size="small" onClick={() => setOpen(false)}>
          <IconCross />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box className="flex flex-col gap-6">
          <Divider />
          <Typography variant="body2">
            Please share general feedback and edit requests for your video within 5 business days.
            See below list for reference on what generally can and cannot be edited. If you are
            unsure about an edit request, please ask. Desire Co. will have edits completed and an
            updated video for your review within 2 business days after the form is sent.
          </Typography>
          <Box className="flex gap-4 py-3">
            <CriteriaColumn title="Editable" editable />
            <CriteriaColumn title="Non Editable" />
          </Box>
          <Typography variant="caption" className="text-text-2">
            *We capture more talking points and B-roll than you&apos;ll see in this video. While we
            lead with what we believe is the strongest edit, let us know if we overlooked a talking
            point from your Intake Form or a possible b-roll shot that you want included
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackCriteriaDialog;
